import { useMemo, useState } from 'react';
// import type { AuthUser } from '@/features/auth/stores/authstore';
import { useAssignRole, useRemoveRole, useUpdateAgronomistRegions } from '../hooks/useUser';
import { useRoles } from '../hooks/useRoles';
import { useCounties } from '@/shared/hooks/useMetaData';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, Shield, ShieldCheck } from 'lucide-react';
import type { AuthUser } from '@/features/auth/stores/authStore';

const AGRONOMIST_ROLE_NAME = 'Agronomist';

interface AssignRoleToUserDialogProps {
  user: AuthUser;
}

export const AssignRoleToUserDialog = ({
  user,
}: AssignRoleToUserDialogProps) => {
  const [open, setOpen] = useState(false);
  const originalRoles = useMemo(() => [...(user.roles || [])].sort(), [user.roles]);
  const originalRegions = useMemo(
    () => [...(user.agronomistRegions || [])].sort((a, b) => a.localeCompare(b)),
    [user.agronomistRegions]
  );
  const [selectedRoles, setSelectedRoles] = useState<string[]>(originalRoles);
  const [agronomistRegions, setAgronomistRegions] = useState<string[]>(originalRegions);
  const [regionError, setRegionError] = useState<string | null>(null);
  const [roleSearch, setRoleSearch] = useState('');

  const assignMutation = useAssignRole();
  const removeMutation = useRemoveRole();
  const updateRegionsMutation = useUpdateAgronomistRegions();
  const rolesQuery = useRoles(1, 100);
  const countiesQuery = useCounties();
  const confirm = useConfirm();
  const isUpdating = assignMutation.isPending || removeMutation.isPending || updateRegionsMutation.isPending;
  const roleItems = rolesQuery.data?.items;
  const roles = useMemo(() => roleItems ?? [], [roleItems]);
  const selectedRoleNames = useMemo(() => new Set(selectedRoles), [selectedRoles]);
  const isAgronomistSelected = selectedRoleNames.has(AGRONOMIST_ROLE_NAME);
  const originalRoleKey = originalRoles.join('|');
  const selectedRoleKey = useMemo(
    () => [...selectedRoles].sort().join('|'),
    [selectedRoles]
  );
  const hasRoleChanges = selectedRoleKey !== originalRoleKey;
  const originalRegionKey = originalRegions.join('|');
  const selectedRegionKey = useMemo(() => agronomistRegions.join('|'), [agronomistRegions]);
  const hasRegionChanges =
    isAgronomistSelected &&
    originalRoles.includes(AGRONOMIST_ROLE_NAME) &&
    selectedRegionKey !== originalRegionKey;
  const hasChanges = hasRoleChanges || hasRegionChanges;
  const filteredRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase();

    if (!term) {
      return roles;
    }

    return roles.filter((role) => {
      const searchable = [role.name, role.description, ...role.permissions]
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [roleSearch, roles]);
  const selectedRolesInCatalog = useMemo(
    () => roles.filter((role) => selectedRoleNames.has(role.name)),
    [roles, selectedRoleNames]
  );
  const selectedPermissionCount = useMemo(
    () =>
      new Set(selectedRolesInCatalog.flatMap((role) => role.permissions)).size,
    [selectedRolesInCatalog]
  );

  const handleRoleToggle = (roleName: string) => {
    setSelectedRoles((prev) => {
      const hasRole = prev.includes(roleName);
      const nextRoles = hasRole
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName];

      if (roleName === AGRONOMIST_ROLE_NAME && hasRole) {
        setAgronomistRegions([]);
        setRegionError(null);
      } else if (roleName === AGRONOMIST_ROLE_NAME) {
        setAgronomistRegions(
          originalRoles.includes(AGRONOMIST_ROLE_NAME) ? originalRegions : []
        );
      }

      return nextRoles;
    });
  };

  const handleRegionToggle = (regionName: string, checked: boolean) => {
    setAgronomistRegions((prev) => {
      const nextRegions = checked
        ? [...prev, regionName]
        : prev.filter((region) => region !== regionName);

      return Array.from(new Set(nextRegions)).sort((a, b) => a.localeCompare(b));
    });

    if (regionError) {
      setRegionError(null);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setSelectedRoles(originalRoles);
      setAgronomistRegions(originalRegions);
      setRegionError(null);
      setRoleSearch('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Determine which roles were added and which were removed
    const addedRoles = selectedRoles.filter(r => !originalRoles.includes(r));
    const removedRoles = originalRoles.filter(r => !selectedRoles.includes(r));

    if (isAgronomistSelected && agronomistRegions.length === 0) {
      setRegionError('At least one region is required for the Agronomist role.');
      return;
    }

    if (addedRoles.length === 0 && removedRoles.length === 0 && !hasRegionChanges) {
      setOpen(false);
      return;
    }

    const userName = `${user.firstName} ${user.otherNames}`.trim() || user.username;

    confirm({
      title: "Update roles?",
      description: `Review the role changes for ${userName} before applying.`,
      confirmLabel: "Update roles",
      body: (
        <div className="space-y-2">
          {addedRoles.length > 0 && (
            <div>
              <p className="font-medium text-green-700">Adding</p>
              <ul className="list-disc pl-5">
                {addedRoles.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              {addedRoles.includes(AGRONOMIST_ROLE_NAME) && agronomistRegions.length > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Regions: {agronomistRegions.join(', ')}
                </p>
              )}
            </div>
          )}
          {removedRoles.length > 0 && (
            <div>
              <p className="font-medium text-red-700">Removing</p>
              <ul className="list-disc pl-5">
                {removedRoles.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {hasRegionChanges && (
            <div>
              <p className="font-medium text-primary">Updating Agronomist regions</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Regions: {agronomistRegions.join(', ')}
              </p>
            </div>
          )}
        </div>
      ),
      onConfirm: () =>
        withConfirmSuccess(
          Promise.all([
            ...removedRoles.map((roleName) =>
              removeMutation.mutateAsync({
                id: user.id,
                payload: { userId: user.id, roleName },
              })
            ),
            ...addedRoles.map((roleName) =>
              assignMutation.mutateAsync({
                id: user.id,
                payload: {
                  userId: user.id,
                  roleName,
                  agronomistRegions:
                    roleName === AGRONOMIST_ROLE_NAME ? agronomistRegions : undefined,
                },
              })
            ),
            ...(hasRegionChanges
              ? [
                  updateRegionsMutation.mutateAsync({
                    id: user.id,
                    payload: {
                      userId: user.id,
                      agronomistRegions,
                    },
                  }),
                ]
              : []),
          ]),
          `Roles updated for ${userName}`
        ).then(() => setOpen(false)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Shield className="w-4 h-4" />
          Assign Roles
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] max-w-[calc(100%-1.5rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b bg-card px-5 py-4 pr-12 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
              <ShieldCheck className="size-5" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle>Assign Roles</DialogTitle>
              <DialogDescription>
                Set access for{' '}
                <span className="font-medium text-foreground">
                  {`${user.firstName} ${user.otherNames}`.trim() || user.username}
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {(assignMutation.error || removeMutation.error || updateRegionsMutation.error) && (
            <div className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 sm:mx-6">
              {(assignMutation.error || removeMutation.error || updateRegionsMutation.error)?.message}
            </div>
          )}

          <div className="shrink-0 space-y-3 border-b bg-background/80 px-5 py-4 sm:px-6">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={roleSearch}
                onChange={(event) => setRoleSearch(event.target.value)}
                placeholder="Search roles or permissions"
                className="bg-card pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary" className="rounded-md">
                {selectedRoles.length} selected
              </Badge>
              <Badge variant="outline" className="rounded-md bg-card">
                {selectedPermissionCount} permissions included
              </Badge>
              {hasChanges && (
                <Badge
                  variant="outline"
                  className="rounded-md border-primary/40 bg-primary/10 text-foreground"
                >
                  Unsaved changes
                </Badge>
              )}
            </div>

            {isAgronomistSelected && (
              <div className="rounded-md border bg-card p-3">
                <div className="mb-2 text-sm font-medium text-foreground">
                  Agronomist regions
                </div>
                {countiesQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading regions...</p>
                ) : (countiesQuery.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No regions available.</p>
                ) : (
                  <div className="grid max-h-52 grid-cols-1 gap-2 overflow-y-auto overscroll-contain pr-1 sm:grid-cols-2">
                    {(countiesQuery.data ?? []).map((county) => {
                      const checked = agronomistRegions.includes(county.name);

                      return (
                        <label key={county.code} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => handleRegionToggle(county.name, Boolean(value))}
                            disabled={isUpdating}
                          />
                          <span>{county.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {regionError && (
                  <p className="mt-2 text-xs text-red-600">{regionError}</p>
                )}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
            {rolesQuery.isLoading ? (
              <LoadingAnimation text="Loading roles..." />
            ) : filteredRoles.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
                No roles match your search.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRoles.map((role) => {
                  const isSelected = selectedRoleNames.has(role.name);

                  return (
                    <Label
                      key={role.id}
                      htmlFor={role.id}
                      className={`grid cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/40 ${
                        isSelected ? 'border-primary/50 bg-primary/10 shadow-xs' : ''
                      }`}
                    >
                      <Checkbox
                        id={role.id}
                        checked={isSelected}
                        onCheckedChange={() => handleRoleToggle(role.name)}
                        disabled={isUpdating}
                        className="mt-1"
                      />
                      <div className="min-w-0 space-y-2">
                        <div className="grid gap-1 sm:grid-cols-[minmax(10rem,0.8fr)_minmax(0,1.2fr)] sm:items-start">
                          <div className="min-w-0 font-medium leading-snug text-foreground">
                            {role.name}
                          </div>
                          <div className="min-w-0 text-xs leading-5 text-muted-foreground">
                            {role.description || 'No description'}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {role.permissions.length === 0 ? (
                            <span className="text-xs text-muted-foreground">
                              No permissions
                            </span>
                          ) : (
                            <>
                              {role.permissions.slice(0, 3).map((perm) => (
                                <Badge
                                  key={perm}
                                  variant="outline"
                                  className="max-w-full rounded-md bg-background px-2 py-0.5 font-normal"
                                >
                                  <span className="truncate">{perm}</span>
                                </Badge>
                              ))}
                              {role.permissions.length > 3 && (
                                <Badge variant="secondary" className="rounded-md">
                                  +{role.permissions.length - 3}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </Label>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t bg-card px-5 py-4 sm:px-6">
            <div className="mr-auto text-sm text-muted-foreground">
              {selectedRoles.length === 0
                ? 'Select at least one role to continue.'
                : hasChanges
                  ? 'Review changes before updating.'
                  : 'No role changes yet.'}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdating || !hasChanges || selectedRoles.length === 0}
            >
              {isUpdating ? 'Updating...' : 'Update Roles'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
